import { useRef, useEffect, useCallback } from 'react';

interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number; // Minimum distance for a swipe (default: 50px)
  velocity?: number;  // Minimum velocity for a swipe (default: 0.3px/ms)
}

interface TouchData {
  startX: number;
  startY: number;
  startTime: number;
  element: EventTarget | null;
}

export const useSwipeGestures = (options: SwipeGestureOptions = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight, 
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    velocity = 0.3
  } = options;

  const touchDataRef = useRef<TouchData | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const handleTouchStart = useCallback((event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;

    touchDataRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      element: event.target
    };
  }, []);

  const handleTouchEnd = useCallback((event: TouchEvent) => {
    const touchData = touchDataRef.current;
    if (!touchData) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchData.startX;
    const deltaY = touch.clientY - touchData.startY;
    const deltaTime = Date.now() - touchData.startTime;
    
    // Calculate velocity
    const velocityX = Math.abs(deltaX) / deltaTime;
    const velocityY = Math.abs(deltaY) / deltaTime;

    // Check if the swipe meets threshold and velocity requirements
    const isHorizontalSwipe = Math.abs(deltaX) > threshold && velocityX > velocity;
    const isVerticalSwipe = Math.abs(deltaY) > threshold && velocityY > velocity;

    // Prevent diagonal swipes by ensuring one axis is dominant
    if (isHorizontalSwipe && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    } else if (isVerticalSwipe && Math.abs(deltaY) > Math.abs(deltaX)) {
      event.preventDefault();
      if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
      } else if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
      }
    }

    touchDataRef.current = null;
  }, [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold, velocity]);

  const handleTouchCancel = useCallback(() => {
    touchDataRef.current = null;
  }, []);

  // Attach event listeners
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Use passive listeners for better performance
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [handleTouchStart, handleTouchEnd, handleTouchCancel]);

  return elementRef;
};